import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import {
  CANONICAL_ACTIVITIES,
  GenericImportService,
  IMPORT_FIELDS,
  type CanonicalActivity,
  type GenericCommitResult,
  type GenericPreviewResponse,
  type ImportTemplate,
  type RowStatus,
} from '../services/generic-import-service';
import { AccountsService } from '../services/accounts-service';
import type { Account } from '../types/api';

const STEPS = ['Upload', 'Columns', 'Activity types', 'Review', 'Done'];

// Token formats, matching the backend's LayoutFromDateFormat vocabulary.
const DATE_FORMATS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-01-15)' },
  { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD (2026/01/15)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/15/2026)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (15/01/2026)' },
  { value: 'DD-MMM-YYYY', label: 'DD-Mon-YYYY (15-Jan-2026)' },
  { value: 'DD-MMM-YY', label: 'DD-Mon-YY (15-Jan-26)' },
  { value: 'MMM D, YYYY', label: 'Mon D, YYYY (Jan 15, 2026)' },
];

const STATUS_CHIP: Record<RowStatus, { label: string; color: 'success' | 'warning' | 'info' | 'error' }> = {
  valid: { label: 'valid', color: 'success' },
  warning: { label: 'warning', color: 'warning' },
  needs_review: { label: 'needs review', color: 'info' },
  error: { label: 'error', color: 'error' },
};

/**
 * Five-step template-driven CSV import.
 *
 * Every "Next" that changes the mapping re-previews on the server rather than
 * re-interpreting rows locally: the rows the user reviews in step 4 are the
 * stored batch the commit will convert, byte for byte.
 */
export const GenericImportWizard: React.FC = () => {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [file, setFile] = useState<File | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState('');
  const [templates, setTemplates] = useState<ImportTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The mapping being edited, seeded from the first preview's suggestion or
  // the chosen template.
  const [preview, setPreview] = useState<GenericPreviewResponse | null>(null);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [dateFormat, setDateFormat] = useState('');
  const [decimalStyle, setDecimalStyle] = useState('plain');

  // Save-as-template
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateSaved, setTemplateSaved] = useState(false);

  // Step 4: per-row answers for needs_review rows.
  const [overrides, setOverrides] = useState<Record<number, CanonicalActivity>>({});
  const [result, setResult] = useState<GenericCommitResult | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const svc = new AccountsService();
        const res = await svc.getAccounts({ per_page: 100 });
        setAccounts(res.data.data || []);
      } catch {
        // account list failing shouldn't block the page; the select stays empty
      }
      try {
        setTemplates(await GenericImportService.listTemplates());
      } catch {
        // same: templates are a convenience
      }
    })();
  }, []);

  const runPreview = async (opts?: { useTemplate?: boolean }) => {
    if (!file || !accountId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await GenericImportService.previewImport(
        file,
        opts?.useTemplate && templateId
          ? { account_id: accountId, template_id: templateId }
          : {
              account_id: accountId,
              column_map: columnMap,
              type_map: typeMap,
              date_format: dateFormat || undefined,
              decimal_style: decimalStyle,
            },
      );
      setPreview(res);
      // Adopt what the server actually parsed with, so the controls show
      // (and edit) reality -- including an auto-detected date format.
      setColumnMap(res.column_map || {});
      setTypeMap(res.type_map || {});
      setDateFormat(res.date_format || '');
      setDecimalStyle(res.decimal_style || 'plain');
      // Overrides belong to a batch; a re-preview invalidates them.
      setOverrides({});
      return res;
    } catch (err: any) {
      setError(err.message || 'Could not read the file');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUploadNext = async () => {
    const res = await runPreview({ useTemplate: true });
    if (res) setStep(1);
  };

  const handleColumnsNext = async () => {
    const res = await runPreview();
    if (res) setStep(2);
  };

  const handleTypesNext = async () => {
    const res = await runPreview();
    if (res) setStep(3);
  };

  const handleCommit = async () => {
    if (!preview) return;
    setLoading(true);
    setError(null);
    try {
      const commit = await GenericImportService.commitBatch(
        preview.batch_id,
        Object.entries(overrides).map(([rowNo, type]) => ({ row_no: Number(rowNo), type })),
      );
      setResult(commit);
      if (saveTemplate && templateName.trim()) {
        try {
          const saved = await GenericImportService.createTemplate({
            name: templateName.trim(),
            column_map: columnMap,
            type_map: typeMap,
            date_format: dateFormat || undefined,
            decimal_style: decimalStyle,
          });
          setTemplates((prev) => [...prev, saved]);
          setTemplateSaved(true);
        } catch {
          // The import itself succeeded; a failed template save must not
          // present as a failed import.
          setTemplateSaved(false);
        }
      }
      setStep(4);
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setFile(null);
    setPreview(null);
    setColumnMap({});
    setTypeMap({});
    setDateFormat('');
    setDecimalStyle('plain');
    setTemplateId('');
    setSaveTemplate(false);
    setTemplateName('');
    setTemplateSaved(false);
    setOverrides({});
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const rows = preview?.rows ?? [];
  const needsReviewUnanswered = rows.filter(
    (r) => r.status === 'needs_review' && !overrides[r.row_no],
  ).length;
  const committable = rows.filter(
    (r) => r.status === 'valid' || r.status === 'warning' || (r.status === 'needs_review' && overrides[r.row_no]),
  ).length;

  return (
    <Box>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map((s) => (
          <Step key={s}>
            <StepLabel>{s}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Step 1: Upload */}
      {step === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Upload a brokerage CSV
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Any broker&apos;s export works: you map the columns once and can save the mapping
                as a template for next time.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()}>
                {file ? file.name : 'Choose file'}
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Account *</InputLabel>
                <Select value={accountId} label="Account *" onChange={(e) => setAccountId(e.target.value)}>
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name} ({a.account_type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Template (optional)</InputLabel>
                <Select value={templateId} label="Template (optional)" onChange={(e) => setTemplateId(e.target.value)}>
                  <MenuItem value="">None — map manually</MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" disabled={!file || !accountId || loading} onClick={handleUploadNext}>
                Next: Map columns
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Columns */}
      {step === 1 && preview && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Map CSV columns
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {preview.headers.length} columns found. Suggestions are pre-filled — confirm or
              correct them. Only Date is required.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              {IMPORT_FIELDS.map((f) => (
                <FormControl key={f.value} size="small" fullWidth>
                  <InputLabel>{f.label + (f.required ? ' *' : '')}</InputLabel>
                  <Select
                    value={columnMap[f.value] || ''}
                    label={f.label + (f.required ? ' *' : '')}
                    onChange={(e) => {
                      const header = e.target.value;
                      setColumnMap((prev) => {
                        const next = { ...prev };
                        if (header) next[f.value] = header;
                        else delete next[f.value];
                        return next;
                      });
                    }}
                  >
                    <MenuItem value="">— not in this file —</MenuItem>
                    {preview.headers.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Date format</InputLabel>
                <Select value={dateFormat} label="Date format" onChange={(e) => setDateFormat(e.target.value)}>
                  <MenuItem value="">Detect automatically</MenuItem>
                  {DATE_FORMATS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Decimal style</InputLabel>
                <Select value={decimalStyle} label="Decimal style" onChange={(e) => setDecimalStyle(e.target.value)}>
                  <MenuItem value="plain">1,234.56</MenuItem>
                  <MenuItem value="european">1.234,56</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} />}
                label="Save this mapping as a template"
              />
              {saveTemplate && (
                <TextField
                  size="small"
                  fullWidth
                  label="Template name"
                  placeholder="e.g. Questrade activity"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleReset}>Cancel</Button>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button
                variant="contained"
                disabled={!columnMap['date'] || loading || (saveTemplate && !templateName.trim())}
                onClick={handleColumnsNext}
              >
                Next: Activity types
              </Button>
            </Box>
            {!columnMap['date'] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                Map the Date column to continue
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Activity types */}
      {step === 2 && preview && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Map activity types
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tell the importer what this broker&apos;s activity codes mean. Codes left as
              UNKNOWN are flagged for review and are not imported until answered.
            </Typography>

            {preview.broker_codes.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No activity-type column is mapped, so every row needs a type chosen at the review
                step. Mapping a Type column usually works better.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Broker&apos;s code</TableCell>
                    <TableCell>Rows</TableCell>
                    <TableCell>Imports as</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.broker_codes.map((bc) => {
                    const current = typeMap[bc.code] ?? bc.mapped;
                    return (
                      <TableRow key={bc.code}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {bc.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{bc.rows}</TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={current}
                              onChange={(e) =>
                                setTypeMap((prev) => ({ ...prev, [bc.code]: e.target.value }))
                              }
                            >
                              {CANONICAL_ACTIVITIES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {current === 'UNKNOWN' && (
                            <Chip label="needs review" color="info" size="small" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button variant="contained" disabled={loading} onClick={handleTypesNext}>
                Next: Review rows
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review */}
      {step === 3 && preview && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Review {rows.length} rows
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {(Object.keys(STATUS_CHIP) as RowStatus[]).map((s) =>
                preview.summary[s] ? (
                  <Chip
                    key={s}
                    label={`${preview.summary[s]} ${STATUS_CHIP[s].label}`}
                    color={STATUS_CHIP[s].color}
                    size="small"
                  />
                ) : null,
              )}
            </Box>
            {needsReviewUnanswered > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {needsReviewUnanswered} row{needsReviewUnanswered === 1 ? '' : 's'} still need a
                type. Unanswered rows are skipped, not imported.
              </Alert>
            )}

            <Box sx={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.row_no} hover>
                      <TableCell>{r.row_no}</TableCell>
                      <TableCell>{r.status === 'error' ? '—' : r.date?.slice(0, 10)}</TableCell>
                      <TableCell>
                        {r.status === 'needs_review' ? (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={overrides[r.row_no] || 'UNKNOWN'}
                              onChange={(e) =>
                                setOverrides((prev) => ({
                                  ...prev,
                                  [r.row_no]: e.target.value as CanonicalActivity,
                                }))
                              }
                            >
                              {CANONICAL_ACTIVITIES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          r.type
                        )}
                        {r.broker_code && r.broker_code.toUpperCase() !== r.type && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            was “{r.broker_code}”
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{r.symbol}</TableCell>
                      <TableCell align="right">{r.quantity !== '0' ? r.quantity : ''}</TableCell>
                      <TableCell align="right">{r.price !== '0' ? r.price : ''}</TableCell>
                      <TableCell align="right">{r.amount !== '0' ? r.amount : ''}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_CHIP[r.status].label}
                          color={STATUS_CHIP[r.status].color}
                          size="small"
                          title={r.message}
                        />
                        {r.message && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220 }}>
                            {r.message}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => setStep(2)}>Back</Button>
              <Button variant="contained" disabled={loading || committable === 0} onClick={handleCommit}>
                {loading ? <CircularProgress size={20} /> : `Import ${committable} rows`}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Done */}
      {step === 4 && result && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" fontWeight={700} color="success.main" sx={{ mb: 2 }}>
              Import complete
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 3 }}>
              <Box>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {result.created}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Imported
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {result.skipped_duplicates}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Already imported
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  {result.skipped_rows}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Skipped
                </Typography>
              </Box>
            </Box>
            {templateSaved && (
              <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
                Template “{templateName}” saved. Next time, pick it at the upload step and the
                mapping is done for you.
              </Alert>
            )}
            {result.errors && result.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Issues ({result.errors.length})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {result.errors.map((e, i) => (
                    <Typography key={i} variant="body2">
                      {e}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}
            <Button variant="contained" onClick={handleReset}>
              Import another file
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
